import React, { useCallback, useEffect, useState } from 'react'

import {
  clearPrevStateSearch,
  fetchSearchResultsByTagsOrStatuses,
  setIsSearchInputActive,
  setIsSearchPage,
  removeFilterTag,
  setFilterTags,
  setIsTagMenuVisible,
  setSearchInputValue,
  setSearchTags,
  setSearchTagsType
} from 'features/common/model/slice/searchSlice'
import {
  selectFilterTags,
  selectSearchTagsSelector,
  selectSearchTagsTypeSelector
} from 'features/common/model/selectors/searchSelectors'
import { SearchTagsType } from 'features/common/model/types/search'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'shared/config/store'
import { Button, Select } from 'shared/ui'
import styled from 'styled-components'
import { buildSearchByTagsUrl } from 'shared/utils/urlUtils'

import { TagInput } from './TagInput'

interface TagMenuProps {
  tagMenuRef: React.RefObject<HTMLElement>
  selectTagsRef: React.RefObject<HTMLElement>
  menuContainerRef: React.RefObject<HTMLDivElement>
}

const stopEvent = (e?: React.MouseEvent) => {
  if (e) {
    e.stopPropagation()
    e.preventDefault()
  }
}

export const TagMenu: React.FC<TagMenuProps> = ({
  tagMenuRef,
  selectTagsRef,
  menuContainerRef
}) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const searchTags = useAppSelector(selectSearchTagsSelector)
  const searchTagsType = useAppSelector(selectSearchTagsTypeSelector)
  const searchTagsTypes = Object.values(SearchTagsType)

  const filterTags = useAppSelector(selectFilterTags)

  const [currentSearchType, setCurrentSearchType] = useState<SearchTagsType>(searchTagsType)

  useEffect(() => {
    dispatch(setFilterTags(searchTags))
  }, [])

  const handleApplyTags = useCallback((e?: React.MouseEvent) => {
    stopEvent(e)

    dispatch(fetchSearchResultsByTagsOrStatuses({
      tagIds: filterTags.map(tag => tag.id),
      searchTagsType: currentSearchType,
      searchType: 'tags'
    }))

    navigate(buildSearchByTagsUrl(filterTags.map(tag => tag.id), currentSearchType))
    dispatch(setIsSearchPage(true))
    dispatch(setIsSearchInputActive(false))
    dispatch(setIsTagMenuVisible(false))
    dispatch(clearPrevStateSearch())
    dispatch(setSearchTagsType(currentSearchType))
    dispatch(setSearchTags(filterTags))
    dispatch(setSearchInputValue(''))
  }, [dispatch, searchTags, filterTags, currentSearchType])

  const handleClearTags = useCallback((e?: React.MouseEvent) => {
    stopEvent(e)
    dispatch(setIsSearchInputActive(true))
    dispatch(setIsTagMenuVisible(true))
    dispatch(setFilterTags([]))
  }, [dispatch, filterTags])

  const handleSearchTypeChange = (type: SearchTagsType) => {
    setCurrentSearchType(type)
  }

  const handleSearchTagsChange = (tag: any) => {
    dispatch(setFilterTags([...filterTags, tag]))
  }

  const handleRemoveTag = (tag: string) => {
    dispatch(removeFilterTag(tag))
  }

  return (
    <STagsMenuContainer ref={tagMenuRef as any}>
      <SSubTitle>
        Filters by tags
      </SSubTitle>

      <SSelect
        ref={selectTagsRef as any}
        defaultValue={currentSearchType}
        onChange={handleSearchTypeChange}
        getPopupContainer={() => menuContainerRef.current || document.body}
        className="search-menu-select"
      >
        {searchTagsTypes.map((type) => (
          <Select.Option
            key={type}
            value={type}
          >
            {type}
          </Select.Option>
        ))}
      </SSelect>

      <TagInput
        currentTags={filterTags}
        isEditable={true}
        placeholder="Search for tags"
        onChange={handleSearchTagsChange}
        onRemoveTag={handleRemoveTag}
      />

      <SButtonsContainer>
        <SButton onClick={handleClearTags}>
          Clear
        </SButton>

        <SButton type="primary"
          disabled={filterTags.length === 0 && !!currentSearchType}
          onClick={handleApplyTags}
        >
          Apply
        </SButton>
      </SButtonsContainer>
    </STagsMenuContainer>
  )
}

const SButtonsContainer = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`

const SButton = styled(Button)`
`

const STagsMenuContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`

const SSubTitle = styled.span`
  overflow: hidden;
  color: rgba(0, 0, 0, 0.45);
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 22px; 
`

const SSelect = styled(Select)`
   &&&&&:hover {
     .ant-select-selector {
       border-color: var(--color-primary);
     }
   } 
`
